import {
  DTOptimizationNode,
  OptimizationNodeOutputData,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {
  type CampaignOptimizationData,
  OptimizeAcrossName,
  OptimizeAcrossValue,
  OptimizeByName,
  OptimizeByValue,
} from 'common/src/modules/optimization/optimization.types';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {getBaseHeader, getDestinationPhrase, getOutputIdentifier} from './utils';

const CONVERSION_OPTIMIZE_BY_VALUES = [
  OptimizeByValue.Conversions,
  OptimizeByValue.ClickThroughConversions,
  OptimizeByValue.ExternalConversions,
];

function isConversionOptimizeBy(optimizeBy: number): boolean {
  return CONVERSION_OPTIMIZE_BY_VALUES.includes(optimizeBy as OptimizeByValue);
}

function formatDropCondition(
  condition: CampaignOptimizationData['dropConditions'][number],
): string {
  return [
    `Minimum Confidence Level: ${condition.minConfidenceLevel}%`,
    `Minimum Days: ${condition.minDays}`,
    `Minimum Impressions: ${condition.minImpressions}`,
    `Minimum Performance Lift: ${condition.minPerformanceLift}%`,
    `Minimum Results: ${condition.minResults}`,
  ].join(', ');
}

export function optimizationCardToText(card: DTOptimizationNode, mode: 'short' | 'full'): string {
  const header = getBaseHeader(card);

  if (mode === 'short') {
    const optimizeByName =
      getMatchingEnumValue(
        OptimizeByValue,
        OptimizeByName,
        card.configuration?.optimizeBy as OptimizeByValue,
      ) ?? '';

    const purpose = `Purpose: Optimize output selection by '${optimizeByName}' rate`;

    const outputLines = card.outputs.map((output: OptimizationNodeOutputData) => {
      const outputId = getOutputIdentifier(output);
      const dest = getDestinationPhrase(output.nodeId);

      return `Output ${outputId} ${dest}`;
    });

    return [header, purpose, ...outputLines].join('\n');
  }

  const config = card.configuration;
  const optimizeByName =
    getMatchingEnumValue(OptimizeByValue, OptimizeByName, config?.optimizeBy as OptimizeByValue) ??
    '';
  const optimizeAcrossName =
    getMatchingEnumValue(
      OptimizeAcrossValue,
      OptimizeAcrossName,
      config?.optimizeGroup as OptimizeAcrossValue,
    ) ?? '';

  const outputLines = card.outputs.map((output: OptimizationNodeOutputData) => {
    const outputId = getOutputIdentifier(output);
    const dest = getDestinationPhrase(output.nodeId);

    return `Output ${outputId} ${dest}`;
  });

  const lines = [
    header,
    `Optimize By: ${optimizeByName}`,
    `Optimize Across: ${optimizeAcrossName}`,
    ...outputLines,
  ];

  if (isConversionOptimizeBy(config?.optimizeBy ?? 0)) {
    if (config?.areAllAttributionSignalsSelected) {
      lines.push('For all attribution signals');
    } else if (config?.attributionSignalIds?.length) {
      lines.push('Only for attribution signals:');

      for (const id of config.attributionSignalIds) {
        lines.push(`- ID ${id}`);
      }
    }
  }

  if (config?.dropConditions?.length) {
    lines.push('Drop Variant Conditions:');

    for (const condition of config.dropConditions) {
      lines.push(formatDropCondition(condition));
    }
  }

  return lines.join('\n');
}
